import { defineStore } from 'pinia';
import { resolveAdapter } from '~/adapters/registry';
import { tryCatch } from '~/utils/apostatudo/try-catch';

type ModalBannerSlug =
  | 'banner-deposito-sem-ftd'
  | 'banner-deposito'
  | 'banner-saque'
  | 'banner-indique'
  | 'login-desktop'
  | 'login-mobile'
  | 'registro-desktop'
  | 'registro-mobile';

type ModalBannerCache = {
  imageUrl: string | null;
  linkUrl: string | null;
  fetchedAt: number;
};

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos

/**
 * Mapeia o tipo do modal + device para a slug do endpoint da API.
 *
 * O depósito não tem um tipo `deposit`: o banner depende do FTD do usuário (ENG-838),
 * então quem chama escolhe entre `deposit-sem-ftd` e `deposit-com-ftd` — ver
 * `useDepositBannerType`. Cada um é uma rota pública própria, igual para todos,
 * e por isso o cache por slug continua valendo.
 *
 * "Com FTD" reaproveita a `banner-deposito`, que já existia para todos antes da
 * ENG-838: o banner que estava no ar segue para quem já depositou, e só a de sem
 * FTD é categoria nova. Trocar a imagem da `banner-deposito` NÃO afeta quem nunca
 * depositou.
 */
const MODAL_SLUG_MAP: Record<
  string,
  { desktop: ModalBannerSlug; mobile: ModalBannerSlug }
> = {
  'deposit-sem-ftd': {
    desktop: 'banner-deposito-sem-ftd',
    mobile: 'banner-deposito-sem-ftd',
  },
  'deposit-com-ftd': {
    desktop: 'banner-deposito',
    mobile: 'banner-deposito',
  },
  withdraw: { desktop: 'banner-saque', mobile: 'banner-saque' },
  login: { desktop: 'login-desktop', mobile: 'login-mobile' },
  register: { desktop: 'registro-desktop', mobile: 'registro-mobile' },
  indique: { desktop: 'banner-indique', mobile: 'banner-indique' },
};

export const useModalBannersStore = defineStore('ap.modal-banners', {
  state: () => ({
    cache: {} as Record<ModalBannerSlug, ModalBannerCache>,
    loading: {} as Record<ModalBannerSlug, boolean>,
  }),

  getters: {
    getImageUrl: (state) => {
      return (modalType: string, isMobile: boolean): string | null => {
        const slugMap = MODAL_SLUG_MAP[modalType];

        if (!slugMap) return null;

        const slug = isMobile ? slugMap.mobile : slugMap.desktop;
        const cached = state.cache[slug];

        if (!cached) return null;

        return cached.imageUrl;
      };
    },

    getLinkUrl: (state) => {
      return (modalType: string, isMobile: boolean): string | null => {
        const slugMap = MODAL_SLUG_MAP[modalType];

        if (!slugMap) return null;

        const slug = isMobile ? slugMap.mobile : slugMap.desktop;

        return state.cache[slug]?.linkUrl ?? null;
      };
    },

    isSlugCached: (state) => {
      return (slug: ModalBannerSlug): boolean => {
        const cached = state.cache[slug];

        if (!cached) return false;

        return Date.now() - cached.fetchedAt < CACHE_TTL_MS;
      };
    },
  },

  actions: {
    async fetchBanner(modalType: string, isMobile: boolean) {
      const slugMap = MODAL_SLUG_MAP[modalType];

      if (!slugMap) return;

      const slug = isMobile ? slugMap.mobile : slugMap.desktop;

      if (this.isSlugCached(slug)) return;
      if (this.loading[slug]) return;

      this.loading[slug] = true;

      const adapter = resolveAdapter('apiFetchPerfilOficialBanners');
      const [banners, error] = await tryCatch(adapter.handle(slug));

      if (error) {
        console.warn(`[ModalBanners] Erro ao buscar banner "${slug}":`, error);
        this.cache[slug] = {
          imageUrl: null,
          linkUrl: null,
          fetchedAt: Date.now(),
        };
        this.loading[slug] = false;

        return;
      }

      const firstBanner = banners?.[0];

      this.cache[slug] = {
        imageUrl: firstBanner?.image || null,
        linkUrl: firstBanner?.linkUrl || null,
        fetchedAt: Date.now(),
      };

      this.loading[slug] = false;
    },

    getSlugForModal(
      modalType: string,
      isMobile: boolean,
    ): ModalBannerSlug | null {
      const slugMap = MODAL_SLUG_MAP[modalType];

      if (!slugMap) return null;

      return isMobile ? slugMap.mobile : slugMap.desktop;
    },
  },
});
